import { MinMaxData } from 'components/charts/types'
import { Granularity } from 'types/contracts'
import { BillShock } from '@boltenergy-be/shared'
import { toBelgianTime } from 'utils/date'

/**
 * generates array of given length with data from an array starting from a certain index
 * @param {number} arraySize
 * @param {(number|null)[]} dataArray
 * @param {[number]} startIndex
 * @returns {(number|null)[]}
 */
export const createChartDataArray = (arraySize: number, dataArray: (number | null)[], startIndex?: number): (number | null)[] => {
  let idx = 0
  return Array.from(Array(arraySize).keys()).reduce((prev: (number | null)[], curr) => {
    if (curr >= (startIndex || 0) && curr <= (startIndex || 0) + dataArray.length - 1) {
      prev.push(dataArray[idx] ?? null)
      idx++
    } else {
      prev.push(null)
    }

    return prev
  }, [])
}

/**
 * calculate min and max values based on array of BillShock data
 * @param {BillShock} data
 * @returns {MinMaxData}
 */
export const getBillingCycleMinMaxRange = (data: BillShock): MinMaxData => {
  let max = 0
  let min = 0

  if (data.settlement.billShockAmount > max) max = data.settlement.billShockAmount
  if (data.settlement.billShockAmount < min) min = data.settlement.billShockAmount

  data.past.forEach((cycle) => {
    if (cycle.billedInstalment > max) max = cycle.billedInstalment
    if (cycle.billedInstalment < min) min = cycle.billedInstalment
  })

  data.future.forEach((cycle) => {
    if (cycle.plannedInstalment > max) max = cycle.plannedInstalment
    if (cycle.proposedInstalment > max) max = cycle.proposedInstalment

    if (cycle.plannedInstalment < min) min = cycle.plannedInstalment
    if (cycle.proposedInstalment < min) min = cycle.proposedInstalment
  })

  max = Math.ceil(max / 100) * 100
  min = Math.floor(min / 100) * 100

  if (min > -50) {
    min = -50
  }

  return { maxDataValue: max, minDataValue: min }
}

/**
 * Formats the chart date label
 *
 * @param {string} label
 * @param {Granularity} granularity
 * @param {boolean} full
 * @returns {string}
 */
export const formatChartDateLabel = (label: string, granularity: Granularity, full?: boolean): string => {
  const date = toBelgianTime(label)

  switch (granularity) {
    case Granularity.MONTH:
      return full ? date.format('MMMM') : date.format('MMM').charAt(0).toUpperCase()

    case Granularity.DAY:
      return full ? date.format('D MMMM YYYY') : date.format('D')

    case Granularity.QUARTER_HOUR:
      return full ? date.format('HH:mm D MMMM YYYY') : date.format('HH:mm')

    case Granularity.HOUR:
      return full ? date.format('H:mm D MMMM YYYY') : date.format('HH:mm')
  }
}
